import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MastheadBackground } from "@/components/MastheadBackground";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Megaphone, Mic, Flag, Heart } from "lucide-react";
import { SEO } from "@/components/SEO";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <SEO />
      <Header />
      
      <main className="flex-grow">
        <section className="relative min-h-screen flex flex-col items-center justify-start overflow-hidden">
          <MastheadBackground />
          
          {/* Hero Image */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full relative"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/50 to-black" />
            <img 
              src="https://cloudflare.slay.events/about_slay.webp"
              alt="SLAY Pride Celebration"
              className="w-full h-[50vh] md:h-[60vh] object-cover"
            />
          </motion.div>

          {/* Content Section - Adjusted spacing */}
          <div className="container mx-auto px-4 text-center relative z-10 mt-16 md:mt-8 pt-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-4xl mx-auto"
            >
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-6 tracking-tight">
                <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500 animate-gradient-x hover:animate-pulse transition-all duration-300">
                  SLAY
                </span>{" "}
                is more than just ticketing
              </h1>
              <p className="text-lg md:text-xl lg:text-2xl mb-8 text-gray-200">
                It's the heartbeat of LGBTQIA+ events worldwide. Whether it's the biggest Pride festivals, underground club nights, drag extravaganzas, or exclusive pop-up raves.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transform hover:-translate-y-1 transition-all duration-200 animate-button-glow text-base md:text-lg px-6 md:px-8 py-4 md:py-6"
                  size="lg"
                >
                  Explore Events
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/create-event')}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white border-none hover:from-pink-600 hover:to-purple-700 transform hover:-translate-y-1 transition-all duration-200 text-base md:text-lg px-8 md:px-10 py-4 md:py-6 hover:text-white animate-button-glow"
                  size="lg"
                >
                  <Megaphone className="w-5 h-5 mr-2" />
                  List Your Event
                </Button>
              </div>
              <p className="text-sm italic text-gray-400 mb-12">
                Australia's #1 LGBTQIA+ ticketing platform for festivals, club nights, and more
              </p>
            </motion.div>
          </div>
        </section>

        {/* Platform Features Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-black to-purple-900/20">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              {/* Feature Card 1 */}
              <div className="bg-black/40 backdrop-blur-lg rounded-xl p-6 md:p-8 border border-purple-500/20 hover:scale-105 transition-transform duration-300 text-center">
                <div className="flex justify-center mb-4">
                  <Mic className="w-10 h-10 md:w-12 md:h-12 text-pink-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Supporting Australian Artists
                </h3>
                <p className="text-gray-300">
                  Empowering local DJs, drag performers, and artists across Sydney, Melbourne, and beyond.
                </p>
              </div>

              {/* Feature Card 2 */}
              <div className="bg-black/40 backdrop-blur-lg rounded-xl p-6 md:p-8 border border-purple-500/20 hover:scale-105 transition-transform duration-300 text-center">
                <div className="flex justify-center mb-4">
                  <Flag className="w-10 h-10 md:w-12 md:h-12 text-purple-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Empowering Queer Venues
                </h3>
                <p className="text-gray-300">
                  Supporting safe spaces & clubs across Australia's vibrant LGBTQIA+ scene.
                </p>
              </div>

              {/* Feature Card 3 */}
              <div className="bg-black/40 backdrop-blur-lg rounded-xl p-6 md:p-8 border border-purple-500/20 hover:scale-105 transition-transform duration-300 text-center">
                <div className="flex justify-center mb-4">
                  <Heart className="w-10 h-10 md:w-12 md:h-12 text-red-500" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                  Community-Driven Impact
                </h3>
                <p className="text-gray-300">
                  Protecting and nurturing LGBTQIA+ spaces across Australia through community funding and support.
                </p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Community Impact Section */}
        <section className="py-16 md:py-20 bg-gradient-to-b from-purple-900/20 to-black">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8">
                SLAY IT FORWARD: GIVING BACK TO THE COMMUNITY
              </h2>
              <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12">
                <div className="p-6 rounded-lg bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-sm">
                  <h4 className="text-lg md:text-xl font-semibold mb-2">LGBTQIA+ Artists</h4>
                  <p className="text-gray-300">Supporting performers, DJs, and drag artists</p>
                </div>
                <div className="p-6 rounded-lg bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-sm">
                  <h4 className="text-lg md:text-xl font-semibold mb-2">Queer Venues</h4>
                  <p className="text-gray-300">Empowering bars, clubs, and event spaces</p>
                </div>
                <div className="p-6 rounded-lg bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-sm">
                  <h4 className="text-lg md:text-xl font-semibold mb-2">Non-Profits</h4>
                  <p className="text-gray-300">Protecting LGBTQIA+ community spaces</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Security Section */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-4">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-2xl p-6 md:p-12 max-w-4xl mx-auto backdrop-blur-lg"
            >
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6 text-center">
                EFFORTLESS, SECURE, & INCLUSIVE TICKETING
              </h2>
              <p className="text-lg md:text-xl text-center text-gray-300 mb-8">
                No Scalpers. No Hassle. No Missing Out.
              </p>
              <div className="flex justify-center">
                <Button 
                  onClick={() => navigate('/')}
                  className="bg-white text-purple-900 hover:bg-gray-200 transform hover:-translate-y-1 transition-all duration-200 text-base md:text-lg px-6 md:px-8 py-3 md:py-4"
                  size="lg"
                >
                  Start Exploring
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
